import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const BASE_URL = API_BASE_PATH;

const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
    ...(isMultipart && { "Content-Type": "multipart/form-data" }),
  };
};

const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  toast.error(detail);
  console.error(detail);
};

const toFormData = (data: Record<string, any>) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value instanceof File ? value : value.toString());
    }
  });
  return formData;
};

// Interfaces
export interface CalendarEvent {
  id: string;
  title: string;
  description?: string | null;
  event_start: string;
  event_end: string;
  event_date?: string;
  location?: string;
  is_company_event?: boolean;
  reminder_minutes_before?: number;
  visibility_type: string;
  repeat_reminder:string;
  assigned_user_ids?: string[];
}

// CREATE
export const createCalendarEvent = async (data: Record<string, any>): Promise<CalendarEvent> => {
  try {
    const formData = toFormData(data);
    const { data: resData } = await axios.post(`${BASE_URL}/create`, formData, {
      headers: getAuthHeaders(true),
    });
    toast.success("Event created ");
    return resData;
  } catch (error) {
    showAxiosError(error, "Couldn't create event");
    throw error;
  }
};

// GET ALL
export const getAllCalendarEvents = async (showAll = false): Promise<CalendarEvent[]> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/events/calander/data`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to load events");
    throw error;
  }
};

// GET EVENTS (generic)
export const getCalendarEvents = async (): Promise<CalendarEvent[]> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/events`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Couldn't fetch events");
    throw error;
  }
};

// GET BY USER
export const getUserCalendarEvents = async (user_id: string): Promise<CalendarEvent[]> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/events/user/${user_id}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Couldn't fetch user events");
    throw error;
  }
};

// UPDATE
export const updateCalendarEvent = async (
  event_id: string,
  data: Record<string, any>
): Promise<CalendarEvent> => {
  try {
    const formData = toFormData(data);
    const { data: resData } = await axios.put(`${BASE_URL}/update/${event_id}`, formData, {
      headers: getAuthHeaders(true),
    });
    toast.success("Event updated ");
    return resData;
  } catch (error) {
    showAxiosError(error, "Update failed");
    throw error;
  }
};
// DELETE
export const deleteCalendarEvent = async (event_id: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/delete/${event_id}`, {
      headers: getAuthHeaders(),
    });
    toast.success("Event deleted ");
  } catch (error) {
    showAxiosError(error, "Failed to delete event");
    throw error;
  }
};